import { getLineage } from "../data/lineages";
import { assetPath } from "../lib/urls";
import { DragonAtmosphere } from "./DragonAtmosphere";

interface DragonArtProps {
  lineage: string;
  portrait: string;
  alt: string;
  signature?: string;
}

const CRESTS: Record<string, string> = {
  red: "M48 32 56 7 62 33 74 3 72 38 91 20 81 47",
  blue: "M46 35 49 5 57 30 69 12 68 39 89 29 78 50",
  green: "M43 39 38 11 53 32 58 7 66 37 84 18 74 51",
  black: "M44 38 34 15 53 31 54 6 65 35 83 12 75 49",
  white: "M46 36 42 8 56 28 63 4 69 37 89 12 77 49",
  gold: "M47 35 55 3 62 30 75 8 71 39 92 19 80 49",
  silver: "M44 37 48 5 58 31 66 8 68 39 88 22 77 50",
  bronze: "M46 36 39 7 55 29 62 11 68 37 87 17 76 48",
  brass: "M44 37 51 9 57 30 70 5 69 39 88 16 76 49",
  copper: "M45 36 36 10 54 30 60 8 67 37 86 21 75 49"
};

function hashText(value: string): number {
  let hash = 2166136261;
  for (let index = 0; index < value.length; index += 1) {
    hash ^= value.charCodeAt(index);
    hash = Math.imul(hash, 16777619);
  }
  return hash >>> 0;
}

function signaturePath(signature: string): string {
  const hash = hashText(signature);
  const left = 18 + (hash % 13);
  const peak = 13 + ((hash >> 4) % 18);
  const right = 70 + ((hash >> 8) % 14);
  const tail = 42 + ((hash >> 12) % 22);
  return `M${left} 78 C31 ${peak} ${right} ${peak + 4} ${82} 72 M25 ${42 + (hash % 9)} C42 ${tail} 55 ${tail - 19} 74 ${48 + ((hash >> 16) % 10)} M42 85 50 ${58 + ((hash >> 20) % 12)} 61 85`;
}

export function DragonArt({ lineage: lineageId, portrait, alt, signature = "" }: DragonArtProps) {
  const lineage = getLineage(lineageId);
  const portraitSrc = portrait ? assetPath(portrait) : "";
  const sigilPath = signaturePath(`${lineage.id}:${signature}`);

  return (
    <div
      className={`dragon-art dragon-art--${lineage.id}`}
      style={
        {
          "--dragon-accent": lineage.accent,
          "--dragon-soft": lineage.accentSoft
        } as React.CSSProperties
      }
    >
      {portraitSrc ? <img src={portraitSrc} alt={alt} className="dragon-art__image" /> : null}

      <DragonAtmosphere accent={lineage.accent} accentSoft={lineage.accentSoft} lineage={lineage.id} />

      <svg className="dragon-art__sigil" viewBox="0 0 100 100" aria-hidden="true" focusable="false">
        <circle cx="50" cy="50" r="38" />
        <path d={sigilPath} />
        <path d="M50 17V28M50 72v11M18 50h11M71 50h11" />
      </svg>

      {portraitSrc ? (
        <svg
          className="dragon-art__silhouette dragon-art__silhouette--overlay"
          viewBox="0 0 180 120"
          aria-hidden="true"
          focusable="false"
        >
          <path
            className="dragon-art__wing"
            d="M111 70 C144 34 165 26 176 28 C160 45 151 65 149 91 C137 75 124 70 111 70Z"
            fill="none"
            stroke={lineage.accentSoft}
            strokeWidth="3"
          />
          <path
            d="M41 81 C51 63 62 50 82 47 C100 44 113 52 128 64 L151 91 C131 85 119 83 106 87 C93 92 85 102 70 103 C54 104 38 98 24 89Z"
            fill="none"
            stroke={lineage.accentSoft}
            strokeWidth="3"
          />
          <path
            d={CRESTS[lineage.id]}
            fill="none"
            stroke={lineage.accent}
            strokeWidth="5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      ) : (
        <svg
          className="dragon-art__silhouette"
          viewBox="0 0 180 120"
          role="img"
          aria-label={alt}
          focusable="false"
        >
          <defs>
            <linearGradient id={`dragon-fill-${lineage.id}`} x1="0" x2="1" y1="0" y2="1">
              <stop offset="0" stopColor={lineage.accentSoft} stopOpacity=".88" />
              <stop offset=".45" stopColor={lineage.accent} stopOpacity=".9" />
              <stop offset="1" stopColor="#100c0a" />
            </linearGradient>
            <filter id="dragon-glow">
              <feGaussianBlur stdDeviation="2.5" result="blur" />
              <feMerge>
                <feMergeNode in="blur" />
                <feMergeNode in="SourceGraphic" />
              </feMerge>
            </filter>
          </defs>
          <path
            className="dragon-art__wing"
            d="M111 70 C144 34 165 26 176 28 C160 45 151 65 149 91 C137 75 124 70 111 70Z"
            fill={`url(#dragon-fill-${lineage.id})`}
          />
          <path
            d="M41 81 C51 63 62 50 82 47 C100 44 113 52 128 64 L151 91 C131 85 119 83 106 87 C93 92 85 102 70 103 C54 104 38 98 24 89Z"
            fill={`url(#dragon-fill-${lineage.id})`}
            stroke={lineage.accentSoft}
            strokeOpacity=".5"
          />
          <path
            d="M38 78 C24 73 13 77 6 84 C20 87 31 91 45 94 C50 86 48 81 38 78Z"
            fill="#16100d"
            stroke={lineage.accent}
            strokeOpacity=".58"
          />
          <path
            d={CRESTS[lineage.id]}
            fill="none"
            stroke={lineage.accentSoft}
            strokeWidth="4"
            strokeLinecap="round"
            strokeLinejoin="round"
            opacity=".8"
          />
          <path d="M45 80 31 83 46 88" fill="none" stroke={lineage.accentSoft} strokeWidth="2" />
          <circle cx="48" cy="75" r="2.6" fill={lineage.accentSoft} filter="url(#dragon-glow)" />
          <path
            d="M88 56 C93 68 92 80 85 96 M104 58 C109 68 109 78 106 87 M121 67 118 83"
            fill="none"
            stroke={lineage.accentSoft}
            strokeOpacity=".32"
          />
        </svg>
      )}

      <div className="dragon-art__veil" />
    </div>
  );
}
